/* platform/Dashboard — Operational landing page. Actionable items only.
   Charts and historical trends live in Platform Analytics. */
import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { StatCard, PageHead, Th } from './_ui';
import { pct, statusTone } from './platformStore';

const SOON_DAYS = 30;
const daysUntil = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

const Section = ({ title, icon, count, countTone, children, viewAll, viewAllLabel }) => (
  <div className="nx-cc-chart-card">
    <div className="nx-cc-chart-head">
      <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <NexusIcon name={icon} size={14} />{title}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {count != null && <Pill tone={countTone || (count > 0 ? 'amber' : 'green')}>{count} {count === 1 ? 'item' : 'items'}</Pill>}
        {viewAll && <button className="nx-panel-link" onClick={viewAll}>{viewAllLabel || 'View all'}<span aria-hidden> →</span></button>}
      </div>
    </div>
    <div style={{ marginTop: 10 }}>{children}</div>
  </div>
);

const EmptyHint = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0', color: 'var(--nx-text-muted)', fontSize: 12 }}>
    <NexusIcon name="CheckCircle2" size={14} style={{ color: 'var(--nx-success)' }} />{text}
  </div>
);

const OrgRow = ({ org, onClick, badge }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--nx-border)', cursor: 'pointer' }} onClick={onClick}>
    <span className="nx-domain-mark" style={{ width: 24, height: 24, fontSize: 10, flexShrink: 0 }}>
      {org.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
    </span>
    <span style={{ flex: 1, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{org.name}</span>
    <span className="nx-muted nx-mini">{org.industry}</span>
    {badge && <Pill tone={badge.tone}>{badge.label}</Pill>}
    <span className="nx-muted nx-mini">{org.created}</span>
  </div>
);

export default function Dashboard({ data }) {
  const navigate    = useNavigate();
  const alertsRef   = useRef(null);
  const { organizations, licenses, audit, workspaces, loading } = data.state;

  if (loading) {
    return (
      <div className="nx-cc-page">
        <PageHead title="Dashboard" sub="Loading platform data…" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginTop: 20 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="nx-cc-skeleton" style={{ height: 72, borderRadius: 8 }} />
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 20 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="nx-cc-skeleton" style={{ height: 180, borderRadius: 10 }} />
          ))}
        </div>
      </div>
    );
  }

  /* ── Actionable lists ──────────────────────────────────────── */
  const pendingOrgs = organizations.filter(o => o.status === 'ORG_ADMIN_PENDING');
  const noAdminOrgs = organizations.filter(o => !o.ownerId && o.status !== 'ARCHIVED');
  const nearingLimitOrgs = organizations.filter(o => {
    const wsCount  = workspaces.filter(w => w.org === o.id).length;
    const userLic  = licenses.find(l => l.org === o.id && l.type === 'USER');
    const userMax  = userLic?.purchased ?? 0;
    if (userMax > 0 && o.users >= userMax * 0.8) return true;
    if (o.maxWorkspaces != null && wsCount >= o.maxWorkspaces * 0.8) return true;
    return false;
  });
  const expiringLicenses = licenses
    .filter(l => { const d = daysUntil(l.renews); return d != null && d >= 0 && d <= SOON_DAYS; })
    .map(l => ({ ...l, daysLeft: daysUntil(l.renews), orgName: organizations.find(o => o.id === l.org)?.name || l.org }))
    .sort((a, b) => a.daysLeft - b.daysLeft);

  /* ── Summary counts ─────────────────────────────────────────── */
  const activeOrgs    = organizations.filter(o => o.status === 'ACTIVE').length;
  const pendingCount  = organizations.filter(o => o.status === 'ORG_ADMIN_PENDING').length;
  const draftCount    = organizations.filter(o => o.status === 'DRAFT').length;
  const totalIssues   = pendingOrgs.length + noAdminOrgs.length + expiringLicenses.length + nearingLimitOrgs.length;

  /* ── Recent provisioning (last 5) ───────────────────────────── */
  const recentOrgs = [...organizations]
    .filter(o => o.created)
    .sort((a, b) => (b.created || '').localeCompare(a.created || ''))
    .slice(0, 5);

  /* ── Recent audit (last 6) ──────────────────────────────────── */
  const recentAudit = audit.slice(0, 6);

  const totalLic = licenses.filter(l => l.type === 'USER').reduce((s, l) => s + (l.purchased ?? 0), 0);
  const usedLic  = organizations.reduce((s, o) => s + o.users, 0);
  const licPct   = pct(usedLic, totalLic);

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Dashboard"
        sub={new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" icon="RefreshCw" onClick={data.reload} disabled={loading}>Refresh</Btn>
            <Btn variant="secondary" icon="Building2" onClick={() => navigate('/platform/organizations')}>
              Organizations
            </Btn>
            <Btn variant="primary" icon="Plus"
              onClick={() => navigate('/platform/organizations', { state: { openCreate: true } })}>
              New Organization
            </Btn>
          </div>
        }
      />

      {/* ── Status overview ──────────────────────────────────── */}
      <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(5,1fr)' }}>
        <StatCard label="Active"     value={activeOrgs}    icon="Check"        color="#0E8C5A" onClick={() => navigate('/platform/organizations')} />
        <StatCard label="Pending"    value={pendingCount}  icon="Clock"        color="#E08A00" onClick={() => navigate('/platform/organizations')} />
        <StatCard label="Draft"      value={draftCount}    icon="FileText"     color="#6E3FBA" onClick={() => navigate('/platform/organizations')} />
        <StatCard label="License Used" value={licPct + '%'} sub={usedLic + '/' + totalLic + ' seats'} icon="Key" color={licPct > 85 ? '#E08A00' : '#0E8C5A'} onClick={() => navigate('/platform/organizations')} />
        <StatCard label="Attention Required" value={totalIssues}  icon="AlertCircle"  color={totalIssues > 0 ? '#C26A1F' : '#0E8C5A'} onClick={() => alertsRef.current?.scrollIntoView({ behavior: 'smooth' })} />
      </div>

      {/* ── Actionable section ────────────────────────────────── */}
      <div ref={alertsRef} style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* Pending orgs — awaiting org admin */}
        <Section title="Awaiting Org Admin Setup" icon="Clock"
          count={pendingOrgs.length} countTone={pendingOrgs.length > 0 ? 'amber' : 'green'}
          viewAll={() => navigate('/platform/organizations')} viewAllLabel="All orgs">
          {pendingOrgs.length === 0
            ? <EmptyHint text="No organizations awaiting setup." />
            : pendingOrgs.slice(0, 5).map(o => (
                <OrgRow key={o.id} org={o} badge={{ tone: 'amber', label: 'Pending' }}
                  onClick={() => navigate('/platform/organizations/' + o.id + '?tab=settings')} />
              ))
          }
        </Section>

        {/* Orgs without admin */}
        <Section title="No Admin Assigned" icon="UserX"
          count={noAdminOrgs.length} countTone={noAdminOrgs.length > 0 ? 'amber' : 'green'}
          viewAll={() => navigate('/platform/organizations')}>
          {noAdminOrgs.length === 0
            ? <EmptyHint text="All organizations have an admin." />
            : noAdminOrgs.slice(0, 5).map(o => (
                <OrgRow key={o.id} org={o} badge={{ tone: statusTone(o.status), label: o.status }}
                  onClick={() => navigate('/platform/organizations/' + o.id)} />
              ))
          }
        </Section>

        {/* Expiring licenses */}
        <Section title="Expiring Licenses" icon="Key"
          count={expiringLicenses.length} countTone={expiringLicenses.length > 0 ? 'amber' : 'green'}
          viewAll={() => navigate('/platform/organizations')}>
          {expiringLicenses.length === 0
            ? <EmptyHint text="No licenses expiring in 30 days." />
            : <table className="nx-table" style={{ marginTop: 4 }}>
                <thead><tr>
                  <Th icon="Building2">Organization</Th>
                  <Th icon="Layers">Type</Th>
                  <Th icon="Clock">Expires In</Th>
                </tr></thead>
                <tbody>{expiringLicenses.slice(0, 5).map(l => (
                  <tr key={l.id} className="nx-tr" style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/platform/organizations/' + l.org)}>
                    <td><span className="nx-row-name-text">{l.orgName}</span></td>
                    <td><Pill tone={l.type === 'USER' ? 'blue' : l.type === 'BOT' ? 'purple' : 'green'}>
                      {({ USER: 'User', BOT: 'Bot', AGENT: 'Agent', CUSTOM: 'Custom' })[l.type] || l.type}
                    </Pill></td>
                    <td><Pill tone={l.daysLeft <= 7 ? 'red' : 'amber'}>{l.daysLeft}d</Pill></td>
                  </tr>
                ))}</tbody>
              </table>
          }
        </Section>

        {/* Nearing limits */}
        <Section title="Nearing Capacity Limits" icon="AlertTriangle"
          count={nearingLimitOrgs.length} countTone={nearingLimitOrgs.length > 0 ? 'amber' : 'green'}
          viewAll={() => navigate('/platform/organizations')}>
          {nearingLimitOrgs.length === 0
            ? <EmptyHint text="All organizations within capacity." />
            : nearingLimitOrgs.slice(0, 5).map(o => {
                const userLicMax = licenses.find(l => l.org === o.id && l.type === 'USER')?.purchased ?? 0;
                const uPct = userLicMax > 0 ? pct(o.users, userLicMax)                                          : 0;
                const wPct = o.maxWorkspaces ? pct(workspaces.filter(w => w.org === o.id).length, o.maxWorkspaces) : 0;
                const p    = Math.max(uPct, wPct);
                const type = uPct >= wPct ? 'Users' : 'Workspaces';
                return (
                  <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--nx-border)', cursor: 'pointer' }}
                    onClick={() => navigate('/platform/organizations/' + o.id)}>
                    <span className="nx-domain-mark" style={{ width: 22, height: 22, fontSize: 10, flexShrink: 0 }}>
                      {o.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
                    </span>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{o.name}</span>
                    <Pill tone="amber">{type}</Pill>
                    <div className="nx-cc-lic-track" style={{ width: 60 }}>
                      <span style={{ width: p + '%', background: p >= 90 ? 'var(--nx-danger)' : '#E08A00' }} />
                    </div>
                    <span style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', width: 32, textAlign: 'right' }}>{p}%</span>
                  </div>
                );
              })
          }
        </Section>

      </div>

      {/* ── Recent provisioning + Activity ───────────────────── */}
      <div className="nx-cc-grid2" style={{ marginTop: 16 }}>

        <Section title="Recently Provisioned" icon="Plus" countTone="blue"
          viewAll={() => navigate('/platform/organizations')} viewAllLabel="View all orgs">
          {recentOrgs.length === 0
            ? <EmptyHint text="No organizations created yet." />
            : recentOrgs.map(o => (
                <OrgRow key={o.id} org={o} badge={{ tone: statusTone(o.status), label: o.status.replace(/_/g, ' ') }}
                  onClick={() => navigate('/platform/organizations/' + o.id)} />
              ))
          }
        </Section>

        <Section title="Recent Activity" icon="Activity"
          viewAll={() => navigate('/platform/audit')} viewAllLabel="Full audit log">
          {recentAudit.length === 0
            ? <EmptyHint text="No audit events recorded." />
            : <div className="nx-cc-activity">
                {recentAudit.map(a => (
                  <div key={a.id} className="nx-cc-activity-row">
                    <span className="nx-cc-activity-who" style={{ minWidth: 60 }}>{a.who}</span>
                    <Pill tone={a.sev === 'warning' ? 'amber' : a.sev === 'critical' ? 'red' : 'blue'}>{a.action}</Pill>
                    <span className="nx-cc-activity-target" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.target}</span>
                    <span className="nx-muted nx-mini" style={{ flexShrink: 0 }}>{a.time}</span>
                  </div>
                ))}
              </div>
          }
        </Section>

      </div>
    </div>
  );
}
